import { useCallback, useEffect, useMemo, useState } from 'react';
import apiService from '../../services/api';

const CHANNEL_OPTIONS = [
  { value: 'EMAIL', label: 'Email' },
  { value: 'IN_APP', label: 'In-App' },
];

const CHANNEL_LABELS = {
  EMAIL: 'Email',
  IN_APP: 'In-App',
  PUSH: 'Push',
};

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function BonusRow({ bonus, onRevoke, revokingId }) {
  const status = bonus.displayStatus || bonus.status;
  const canRevoke = status === 'ACTIVE';

  return (
    <div className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900">
          +{bonus.quantity} {CHANNEL_LABELS[bonus.channel] || bonus.channel}
        </p>
        <p className="mt-0.5 text-xs text-gray-600">{bonus.reason}</p>
        <p className="mt-1 text-xs text-gray-500">
          Accordé le {formatDate(bonus.createdAt)}
          {bonus.expiresAt ? ` · Fin ${formatDate(bonus.expiresAt)}` : ' · Sans date de fin'}
          {status === 'REVOKED' && bonus.revokedAt ? ` · Révoqué le ${formatDate(bonus.revokedAt)}` : ''}
        </p>
      </div>
      {canRevoke ? (
        <button
          type="button"
          className="btn-secondary text-xs whitespace-nowrap disabled:opacity-50"
          disabled={revokingId === bonus.id}
          onClick={() => onRevoke(bonus.id)}
        >
          {revokingId === bonus.id ? 'Révocation…' : 'Révoquer'}
        </button>
      ) : (
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{status}</span>
      )}
    </div>
  );
}

export default function CommunicationBonusAdminPanel({ commercantId, entitlements = null, onUpdated }) {
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    channel: 'EMAIL',
    quantity: '1',
    reason: '',
    expiresAt: '',
  });

  const load = useCallback(async () => {
    if (!commercantId) return;
    try {
      setLoading(true);
      setError('');
      const res = await apiService.getGrowthBonuses(commercantId);
      setBonuses(Array.isArray(res?.bonuses) ? res.bonuses : []);
    } catch (err) {
      setError(err?.message || 'Impossible de charger les bonus.');
    } finally {
      setLoading(false);
    }
  }, [commercantId]);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => {
    const active = [];
    const expired = [];
    const revoked = [];
    for (const bonus of bonuses) {
      const status = bonus.displayStatus || bonus.status;
      if (status === 'ACTIVE') active.push(bonus);
      else if (status === 'REVOKED') revoked.push(bonus);
      else expired.push(bonus);
    }
    return { active, expired, revoked };
  }, [bonuses]);

  const capacityHint = useMemo(() => {
    if (!entitlements) return null;
    const stdEmail = entitlements.standard?.email ?? entitlements.caps?.emailCampaignsPerMonth ?? 0;
    const stdInApp = entitlements.standard?.inApp ?? entitlements.caps?.inAppCampaignsPerMonth ?? 0;
    const bonusEmail = entitlements.bonus?.email ?? 0;
    const bonusInApp = entitlements.bonus?.inApp ?? 0;
    const capEmail = entitlements.capacity?.email ?? stdEmail + bonusEmail;
    const capInApp = entitlements.capacity?.inApp ?? stdInApp + bonusInApp;
    return `Capacité actuelle : Email ${stdEmail} inclus + ${bonusEmail} offerts = ${capEmail} · In-App ${stdInApp} inclus + ${bonusInApp} offerts = ${capInApp}`;
  }, [entitlements]);

  const submit = async (event) => {
    event.preventDefault();
    if (!commercantId || saving) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const res = await apiService.createGrowthBonus({
        commercantId,
        channel: form.channel,
        quantity: Number(form.quantity),
        reason: form.reason.trim(),
        expiresAt: form.expiresAt || null,
      });
      setSuccess('Bonus accordé. La capacité du commerçant est mise à jour immédiatement.');
      setForm({ channel: 'EMAIL', quantity: '1', reason: '', expiresAt: '' });
      if (res?.bonus) {
        setBonuses((prev) => [res.bonus, ...prev]);
      } else {
        await load();
      }
      if (typeof onUpdated === 'function' && res?.entitlements) {
        await onUpdated({ entitlements: res.entitlements });
      }
    } catch (err) {
      setError(err?.message || 'Impossible d’accorder le bonus.');
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (bonusId) => {
    if (!bonusId || revokingId) return;
    try {
      setRevokingId(bonusId);
      setError('');
      setSuccess('');
      const res = await apiService.revokeGrowthBonus(bonusId);
      setSuccess('Bonus révoqué. L’usage déjà consommé ce mois-ci est conservé.');
      setBonuses((prev) =>
        prev.map((item) => (item.id === bonusId ? res.bonus || { ...item, status: 'REVOKED' } : item))
      );
      if (typeof onUpdated === 'function' && res?.entitlements) {
        await onUpdated({ entitlements: res.entitlements });
      }
    } catch (err) {
      setError(err?.message || 'Impossible de révoquer le bonus.');
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50/40 px-4 py-4 space-y-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">Bonus Communication</h4>
        <p className="mt-1 text-xs text-gray-600">
          Accordez des campagnes supplémentaires sans modifier l’offre Inclus / Plus ni les quotas standards.
        </p>
        {capacityHint ? <p className="mt-2 text-xs text-sky-900/80">{capacityHint}</p> : null}
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded-md border border-sky-100 bg-white p-3">
        <label className="block text-xs text-gray-600">
          Canal
          <select
            className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
            value={form.channel}
            onChange={(e) => setForm((prev) => ({ ...prev, channel: e.target.value }))}
          >
            {CHANNEL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-gray-600">
          Quantité
          <input
            type="number"
            min={1}
            step={1}
            className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
            value={form.quantity}
            onChange={(e) => setForm((prev) => ({ ...prev, quantity: e.target.value }))}
            required
          />
        </label>
        <label className="block text-xs text-gray-600 md:col-span-2">
          Motif (obligatoire)
          <input
            type="text"
            className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
            value={form.reason}
            onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))}
            placeholder="Ex. lancement été, geste commercial…"
            required
            minLength={3}
          />
        </label>
        <label className="block text-xs text-gray-600 md:col-span-2">
          Date de fin (optionnelle)
          <input
            type="date"
            className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
            value={form.expiresAt}
            onChange={(e) => setForm((prev) => ({ ...prev, expiresAt: e.target.value }))}
          />
        </label>
        <div className="md:col-span-2 flex justify-end">
          <button type="submit" className="btn-primary text-sm disabled:opacity-50" disabled={saving}>
            {saving ? 'Ajout…' : 'Ajouter un bonus'}
          </button>
        </div>
      </form>

      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      {success ? <p className="text-sm text-green-700">{success}</p> : null}

      {loading ? (
        <p className="text-sm text-gray-500">Chargement des bonus…</p>
      ) : (
        <div className="space-y-4">
          <section className="space-y-2">
            <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Actifs</h5>
            {grouped.active.length === 0 ? (
              <p className="text-xs text-gray-500">Aucun bonus actif.</p>
            ) : (
              grouped.active.map((bonus) => (
                <BonusRow key={bonus.id} bonus={bonus} onRevoke={revoke} revokingId={revokingId} />
              ))
            )}
          </section>
          {grouped.expired.length > 0 && (
            <section className="space-y-2">
              <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Expirés</h5>
              {grouped.expired.map((bonus) => (
                <BonusRow key={bonus.id} bonus={bonus} onRevoke={revoke} revokingId={revokingId} />
              ))}
            </section>
          )}
          {grouped.revoked.length > 0 && (
            <section className="space-y-2">
              <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Révoqués</h5>
              {grouped.revoked.map((bonus) => (
                <BonusRow key={bonus.id} bonus={bonus} onRevoke={revoke} revokingId={revokingId} />
              ))}
            </section>
          )}
        </div>
      )}
    </div>
  );
}
